import { ActionTree, Commit } from 'vuex';
import { State } from './detail.store';
import graphQl from '@/services/graphQl';
import { AxiosResponse } from 'axios';
import graphHelpers from '@/utils/graphHelper';
import { Context, Params } from '@/utils/types/global';
import { CloudService } from '@/services/api/integration.service';
import { parseServiceObjFromApplication } from '@/utils/helper-function';

const actions: ActionTree<State, any> = {
  async fetchAwsServices(
    context: Context,
    params: { duration: any; serviceReq: any },
  ) {
    if (!params.serviceReq) {
      params.serviceReq = {
        capability: 'C',
        subType: 'RDS',
      };
    }
    const { data } = await CloudService.getAwsAccountConnectionNames();
    const filteredServices: any[] = [];

    const services = await graphQl
      .query('getServiceForTeamByDuration')
      .params(params)
      .then((res: AxiosResponse) => {
        const { services } = res.data.data;
        return services;
      });

    services.forEach((service) => {
      if (data.connectionNames[service.label]) {
        filteredServices.push({
          key: service.key,
          label: data.connectionNames[service.label],
        });
      }
    });

    return filteredServices;
  },

  async getGraphData(context: Context, params: Params) {  
    const { duration } = params;
    const {
      currentService,
      currentServiceInstance,
      currentServices,
      clusterLabels,
    } = context.rootState.cloudState.detail;
    const { cloudSvc: currentSubType } = context.rootState.cloudState.services;
    const { currentPage } = context.rootState.cloudState.detail;
    let variables = {};
    const currentSubscriptionIds = currentServices.map((service: string) => {
      return parseServiceObjFromApplication({ key: service, label: '' });
    });
    const { data } = await CloudService.getAwsAccountConnectionNames();
    // Use graphHelpers.createConfigDatabase to generate config object
    const config = graphHelpers.createConfigCloud(context, params);
    // Check if config or required fields are missing
    if (!config || !config.metricName || !config.queryMetricType) {
      console.error('Missing configuration for graph data');
      return;
    }

    const metricNames = graphHelpers.parse(config.metricName);

    // Determine if currentServiceInstance is an array or a single value
    let serviceInstances = Array.isArray(currentServiceInstance)
      ? currentServiceInstance
      : [currentServiceInstance];

    // Set up variables based on currentService and serviceInstances
    const variablesList: any[] = [];

    // Handle based on entityType
    if (config.entityType === 'ServiceInstance') {
      metricNames.forEach((metricName: string) => {
        serviceInstances.forEach((instance: string) => {
          variablesList.push({
            duration: params.duration,
            condition: {
              name: metricName,
              entity: {
                scope: 'ServiceInstance',
                serviceName: currentService,
                serviceInstanceName: instance,
                normal: true,
              },
            },
          });
        });
      });
    } else if (config.entityType === 'Service') {
      currentServices.forEach((instance: string) => {
        variablesList.push({
          duration: params.duration,
          condition: {
            name: config.metricName,
            entity: {
              scope: 'Service',
              serviceName: instance,
              normal: true,
            },
          },
        });
      });
    }
    try {
      if (config.entityType === 'ServiceInstance') {
        // Perform GraphQL queries using Promise.all for parallel execution
        const promises = variablesList.map((variables) => {
          return graphQl
            .graphQuery(variables, config.queryMetricType)
            .then((res: AxiosResponse<any>) => {
              const resData = res.data.data;
              return {
                ...resData,
                config,
                metricName: variables.condition.name,
              };
            });
        });
        const results = await Promise.all(promises);
        return results;
      } else if (config.entityType === 'Service') {
        const promises = variablesList.map((variables, index) => {
          return graphQl
            .graphQuery(variables, config.queryMetricType)
            .then((res: AxiosResponse<any>) => {
              const resData = res.data.data;
              return {
                ...resData,
                config,
                metricName:
                  currentSubType === 'ELASTICACHE' && currentPage === 'subscriptionId'
                    ? clusterLabels[index]
                    : data.connectionNames[
                        currentSubscriptionIds[index].name
                      ] || currentSubscriptionIds[index].name,
              };
            });
        });
        const results = await Promise.all(promises);

        return results;
      }
    } catch (error) {
      console.error('Error fetching graph data:', error);
      throw error;
    }
  },

  async getGraphDataForCustomDashboard(context: Context, params: any) {
    const { config, duration, chartConfig } = params;
    const { data } = await CloudService.getAwsAccountConnectionNames();
    const variableList: any[] = [];
    if (chartConfig.entityType === "ServiceInstance") {
      chartConfig.instanceIds.forEach((instance) => {
        variableList.push({
          duration: duration,
          condition: {
            name: chartConfig.metricName,
            entity: {
              scope: "ServiceInstance",
              serviceName: chartConfig.serviceKey,
              serviceInstanceName: instance.value,
              normal: true,
            },
          },
        });
      });
    } else if (chartConfig.entityType === "Service") {
      chartConfig.instanceIds.forEach((instance) => {
        variableList.push({
          duration: duration,
          condition: {
            name: chartConfig.metricName,
            entity: {
              scope: "ServiceInstance",
              serviceName: chartConfig.serviceKey,
              serviceInstanceName: instance.value,
              normal: true,
            },
          },
        });
      });
    }
    if (chartConfig.entityType === "ServiceInstance") {
      const promises = variableList.map((variables) => {
        return graphQl
          .graphQuery(variables, chartConfig.queryMetricType)
          .then((res: AxiosResponse<any>) => {
            const resData = res.data.data;
            return {
              ...resData,
              chartConfig,
              metricName: variables.condition.name,
            };
          });
      });
      const results = await Promise.all(promises);
      return results;
    } else if (chartConfig.entityType === "Service") {
      const promises = variableList.map((variables, index) => {
        return graphQl
          .graphQuery(variables, chartConfig.queryMetricType)
          .then((res: AxiosResponse<any>) => {
            const resData = res.data.data;
            return {
              ...resData,
              config: chartConfig,
              metricName: chartConfig.instanceIds[index].keyLabel,
            };
          });
      });

      const results = await Promise.all(promises);
      return results;
    }
  },

  async getGraphDataForTableGraph(
    context: { commit: Commit; rootState: any },
    params: { serviceReq: any; duration: any },
  ) {
    try {
      if (!params) {
        return []; // If params are not provided, return an empty array
      }

      const response: any = await graphQl
        .query('getServiceInstancesByMetaProperties')
        .params(params);

      // Extract instanceId from the response or default to an empty array if not found
      return response?.data?.data?.instanceId ?? [];
    } catch (error) {
      console.error('Error fetching MQ instance:', error);
      throw error; // Rethrow the error to handle it in the calling context
    }
  },

  async fetchAwsServiceInstances(context: Context, params: { serviceId: any }) {
    return graphQl
      .query('queryServiceInstances')
      .params(params)
      .then((res: AxiosResponse) => {
        const databaseInstances = res.data.data.instanceId;
        return databaseInstances;
      });
  },

  async fetchAwsServiceInstancesWithInstanceType(context: Context, params: { serviceId: any }) {
    return graphQl
      .query('getServiceInstancesWithInstanceType')
      .params(params)
      .then((res: AxiosResponse) => {
        const databaseInstances = res.data.data.instanceId;
        return databaseInstances;
      });
  },

  async fetchReadMatricDataForServices(_, params) {
    const { headers, iterateInstance, getDurationTime } = params;
    let allMetrices: any[] = [];
    const metricNameDataTypeMapper = {};
    const metrics: any[] = [];
    const serviceIds = iterateInstance.map((instance) => instance.key);

    headers.forEach((header) => {
      metrics.push(header.key);
      metricNameDataTypeMapper[header.key] = header.valueType;
    });

    const { data: connectionNameMapper } =
      await CloudService.getAwsAccountConnectionNames();

    const { data }: any = await graphQl
      .query('getServiceMetricsAggregationByList')
      .params({
        condition: { metrics, serviceIds },
        duration: getDurationTime,
      });
    allMetrices = [
      ...data.data.getServiceMetricsAggregationByList.map((serviceData) => {
        const obj = {};
        serviceData.values.forEach((value) => {
          obj[value.metricName] =
            value[metricNameDataTypeMapper[value.metricName]];
        });
        return {
          ...obj,
          noOfDbInstance: serviceData.values[0].instanceCount,
          subscriptionId:
            connectionNameMapper.connectionNames[serviceData.subscriptionId],
        };
      }),
    ];

    return allMetrices;
  },

  async fetchReadMatricListDataForServices(context, params) {
    const { headers, iterateInstance, getDurationTime, serviceId } = params;
    const allMetrices: any[] = [];
    const metricList = headers.map((header) => header.key);
    for (let service of iterateInstance) {
      const instances = await context.dispatch('fetchAwsServiceInstances', {
        serviceId: service.key,
        duration: getDurationTime,
      });
      const promises: any[] = [];
      let matricsDataPerSubscriptionID: any = {};
      promises.push(
        graphQl.query('getInstanceMetricsAggregationList').params({
          condition: {
            name: '',
            entity: {
              scope: 'Service',
              normal: true,
              serviceName: service.key,
            },
          },
          duration: getDurationTime,
          metricList,
        }),
      );

      const promiseResponse = await Promise.allSettled(promises);
      promiseResponse.forEach((response, index) => {
        if (response.status === 'fulfilled' && index !== 5) {
          response.value.data.data.readMetricsValueList.forEach(
            (value) =>
              (matricsDataPerSubscriptionID[value.label] = value.value),
          );
        }
      });
      allMetrices.push({
        ...matricsDataPerSubscriptionID,
        subscriptionId: service.label,
        serviceId: service.key,
        lambda_function_count: instances.length,
      });
    }
    return allMetrices;
  },

  async fetchReadMatricDataForInstances(_, params) {
    const { headers, iterateInstance, getDurationTime, serviceId } = params;
    const allMetrices: any[] = [];
    const metricList = headers.map((header) => header.key);
    for (let service of iterateInstance) {
      const promises: any[] = [];
      let matricsDataPerSubscriptionID: any = {};
      promises.push(
        graphQl.query('getInstanceMetricsAggregationList').params({
          condition: {
            name: '',
            entity: {
              scope: 'ServiceInstance',
              normal: true,
              serviceName: serviceId,
              serviceInstanceName: service.key,
            },
          },
          duration: getDurationTime,
          metricList,
        }),
      );

      const promiseResponse = await Promise.allSettled(promises);
      promiseResponse.forEach((response, index) => {
        if (response.status === 'fulfilled' && index !== 5) {
          response.value.data.data.readMetricsValueList.forEach(
            (value) =>
              (matricsDataPerSubscriptionID[value.label] = value.value),
          );
        }
      });

      allMetrices.push({
        databaseId: service.label,
        ...matricsDataPerSubscriptionID,
      });
    }

    return allMetrices;
  },

  async getTopAlertsPerService(context, params) {
    const variables = {
      condition: params.condition,
      duration: params.duration,
    };
    const { data } = await graphQl
      .query('getTopAlertsPerService')
      .params(variables);
    return data;
  },

  async getTopAlertsPerInstances(context, params) {
    const variables = {
      condition: params.condition,
      duration: params.duration,
    };
    const { data } = await graphQl
      .query('getTopAlertsPerInstances')
      .params(variables);
    return data;
  },

  async getMetricValueByServiceId(context, params) {
    const variables = {
      condition: params.condition,
      duration: params.duration,
    };
    const { data } = await graphQl
      .query('getMetricValueByServiceId')
      .params(variables);
    return data;
  },

  async fetchLogsData(context, params) {
    const variables = {
      condition: params.condition,
      duration: params.duration,
      pagination: params.pagination,
    };
    const { data } = await graphQl
      .query('getLambdaFunctionsLogs')
      .params(variables);
    return data;
  },
};

export default actions;
